import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMobileAuth } from '../../context/MobileAuthContext';
import { useMobileChat } from '../../context/MobileChatContext';
import { formatTime as formatTimeUtil, formatWeekdayShort } from '../../utils/dateFormatting';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Briefcase,
  Clock,
  LogOut,
  ChevronRight,
  Calendar,
  CalendarDays,
  Loader2,
  MapPin,
  Truck,
  Bell,
  MessageSquare,
  ClipboardList,
  ClipboardCheck,
  HelpCircle,
  Shield,
  ShieldCheck,
  FileCheck,
  FileText,
  FolderOpen,
  KeyRound,
  Settings,
  Sparkles,
  Sun,
  Moon,
} from 'lucide-react';
import AddToHomeScreen from '../../components/mobile/AddToHomeScreen';
import MobileLayout from '../../components/mobile/MobileLayout';
import { MobilePinSetupDialog } from '../../components/mobile/MobilePinSetupDialog';
import { MobileClockWidget } from '../../components/mobile/MobileClockWidget';
import { useMobileTheme } from '../../context/MobileThemeContext';

const MobileDashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { operator, api, logout, hasFeature, hasMobileQuotesAccess, deviceToken, pendingPinSetup, tenantSettings, canAccessSchedule, isWorker, isTimesheetOnly, isTenantAdmin, isFlagEnabled, can, isAdmin } = useMobileAuth();
  const { unreadCount: chatUnreadCount, fetchRooms } = useMobileChat();
  const { isDark, toggleTheme } = useMobileTheme();
  const [todayJobs, setTodayJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showPinBanner, setShowPinBanner] = useState(true);
  const [showPinSetup, setShowPinSetup] = useState(false);
  const [adminStats, setAdminStats] = useState(null);
  const timesheetOnly = isTimesheetOnly();

  useEffect(() => {
    if (isWorker() && !timesheetOnly) {
      fetchTodayJobs();
      fetchNotificationCount();
    } else {
      setLoading(false);
    }
    fetchRooms(); // Fetch chat rooms to get unread count

    // Push notifications are handled automatically by PushService in the native app
    // No manual permission prompt needed
    
    // Poll for new notifications every 30 seconds (only for workers)
    const interval = isWorker() ? setInterval(() => {
      fetchNotificationCount();
      fetchRooms();
    }, 30000) : null;
    return () => { if (interval) clearInterval(interval); };
  }, []);

  // Fetch admin stats once the operator is hydrated (avoids a mount race where
  // canAccessSchedule() is false because operator isn't loaded yet).
  useEffect(() => {
    if (operator && canAccessSchedule()) {
      fetchAdminStats();
    }
  }, [operator]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTodayJobs = async () => {
    try {
      const now = new Date();
      const userToday = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      // New upcoming endpoint — returns Today + next 6 days (7-day window),
      // statuses sent → invoiced, one card per (job, viewing_date).
      const response = await api.get(`/api/mobile/jobs/upcoming?days=7&user_today=${userToday}`);
      setTodayJobs(response.data);
    } catch (error) {
      console.error('Failed to fetch upcoming jobs');
    } finally {
      setLoading(false);
    }
  };

  const fetchNotificationCount = async () => {
    try {
      const response = await api.get('/api/mobile/notifications/count');
      setUnreadCount(response.data.unread_count);
    } catch (error) {
      console.error('Failed to fetch notification count');
    }
  };

  const fetchAdminStats = async () => {
    try {
      const res = await api.get('/api/jobs');
      const jobs = res.data || [];
      const now = new Date();
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      const todaysList = jobs.filter(j => today >= j.start_date && today <= (j.end_date || j.start_date));
      setAdminStats({
        total: todaysList.length,
        unallocated: todaysList.filter(j => j.status === 'not_allocated' || j.status === 'partially_allocated').length,
        allocated: todaysList.filter(j => ['allocated', 'sent', 'mixed'].includes(j.status)).length,
        confirmed: todaysList.filter(j => j.status === 'confirmed').length,
        docketed: todaysList.filter(j => ['docketed', 'invoiced'].includes(j.status)).length,
      });
    } catch {
      // Silently fail — admin stats are non-critical
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/mobile/login');
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    return formatTimeUtil(timeStr, tenantSettings.time_format);
  };

  return (
    <MobileLayout>
      {/* Header */}
      <div style={{ background: 'var(--m-bg-card)' }} className="px-4 py-4 sticky top-0 z-10 safe-top">
        <div className="flex items-center justify-between">
          <div>
            <p style={{ color: 'var(--m-text-secondary)' }} className="text-xs">{t('mobile.dashboard.welcome')}</p>
            <h1 style={{ color: 'var(--m-text)' }} className="font-bold text-lg" data-testid="dashboard-operator-name">{operator?.name}</h1>
            {operator?.company_name && (
              <p style={{ color: 'var(--m-text-secondary)' }} className="text-xs mt-0.5" data-testid="dashboard-tenant-name">{operator.company_name}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleTheme}
              className={isDark ? 'text-[var(--m-text-secondary)] hover:text-[var(--m-text)]' : 'text-slate-500 hover:text-slate-700'}
              data-testid="mobile-theme-toggle"
            >
              {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            {/* Settings Button */}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/mobile/settings')} 
              className={isDark ? 'text-[var(--m-text-secondary)] hover:text-[var(--m-text)]' : 'text-slate-500 hover:text-slate-700'}
              data-testid="dashboard-settings-button"
            >
              <Settings className="h-5 w-5" />
            </Button>
            {/* Notification Bell */}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/mobile/notifications')} 
              className="text-[var(--m-text-secondary)] relative"
            >
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-[#ffd108] text-[#1a1a1a] text-xs font-bold rounded-full flex items-center justify-center px-1">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Button>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-[var(--m-text-secondary)]">
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* PIN Setup Banner - Show when PIN is not set */}
        {!deviceToken && showPinBanner && (
          <Card className="bg-gradient-to-r from-[#ffd108]/20 to-[#ffd108]/5 border-[#ffd108]/30" data-testid="pin-setup-banner">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[#ffd108]/20 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="h-5 w-5 text-[#ffd108]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-[var(--m-text)] font-medium text-sm">Enable Quick Login</h3>
                  <p className="text-[var(--m-text-secondary)] text-xs mt-0.5">Set up a PIN for faster access next time</p>
                  <div className="flex gap-2 mt-3">
                    <Button 
                      size="sm"
                      onClick={() => setShowPinSetup(true)}
                      className="btn-volt text-xs h-8"
                      data-testid="banner-setup-pin-button"
                    >
                      <KeyRound className="h-3.5 w-3.5 mr-1.5" />
                      Set Up PIN
                    </Button>
                    <Button 
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowPinBanner(false)}
                      className="text-[var(--m-text-secondary)] hover:text-[var(--m-text)] text-xs h-8"
                      data-testid="banner-dismiss-button"
                    >
                      Later
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <MobilePinSetupDialog open={showPinSetup} onOpenChange={setShowPinSetup} />

        {/* Clock On / Clock Off — workers & timesheet-only users with the timesheets feature */}
        {isWorker() && hasFeature('timesheets') && <MobileClockWidget />}

        {/* Push Notification Permission Banner — removed, native push registers automatically */}

        {/* Today's Summary - for tenant admin/dispatchers */}
        {canAccessSchedule() && adminStats && (
          <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]" data-testid="admin-today-summary">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="h-4 w-4 text-[#ffd108]" />
                <h3 className="text-[var(--m-text)] font-semibold text-sm">Today's Summary</h3>
                <Badge variant="secondary" className="bg-[#ffd108] text-[#1a1a1a] ml-auto">{adminStats.total}</Badge>
              </div>
              <div className="grid grid-cols-4 gap-2">
                <div className="text-center p-2 bg-[var(--m-bg)] rounded-lg">
                  <p className="text-red-400 font-bold text-lg">{adminStats.unallocated}</p>
                  <p className="text-[var(--m-text-muted)] text-xs">Unalloc</p>
                </div>
                <div className="text-center p-2 bg-[var(--m-bg)] rounded-lg">
                  <p className="text-blue-400 font-bold text-lg">{adminStats.allocated}</p>
                  <p className="text-[var(--m-text-muted)] text-xs">Allocated</p>
                </div>
                <div className="text-center p-2 bg-[var(--m-bg)] rounded-lg">
                  <p className="text-green-400 font-bold text-lg">{adminStats.confirmed}</p>
                  <p className="text-[var(--m-text-muted)] text-xs">Confirmed</p>
                </div>
                <div className="text-center p-2 bg-[var(--m-bg)] rounded-lg">
                  <p className="text-cyan-400 font-bold text-lg">{adminStats.docketed}</p>
                  <p className="text-[var(--m-text-muted)] text-xs">Docketed</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Unified Quick Actions Grid — each button shows/hides by role & permissions */}
        {(() => {
          const actions = [
            { key: 'schedule', icon: Calendar, label: 'Schedule', onClick: () => navigate('/mobile/schedule'), show: can('schedule.read') && !timesheetOnly, testId: 'schedule-btn' },
            { key: 'jobs', icon: Briefcase, label: t('mobile.menu.jobs'), onClick: () => navigate('/mobile/jobs'), show: isWorker() && !timesheetOnly, testId: 'jobs-btn' },
            { key: 'timesheet', icon: Clock, label: t('mobile.menu.timesheet'), onClick: () => navigate('/mobile/timesheet'), show: isWorker() || can('timesheets.read'), locked: !hasFeature('timesheets'), testId: 'timesheet-btn' },
            { key: 'leave', icon: CalendarDays, label: 'Leave', onClick: () => navigate('/mobile/leave'), show: isWorker() && hasFeature('worker_availability'), testId: 'leave-btn' },
            { key: 'forms', icon: ClipboardList, label: 'Forms', onClick: () => navigate('/mobile/forms'), show: (isWorker() || can('forms.read')) && !timesheetOnly, locked: !hasFeature('forms'), testId: 'forms-btn' },
            { key: 'manage', icon: ShieldCheck, label: 'Manage', onClick: () => navigate('/mobile/manage'), show: isTenantAdmin() && isFlagEnabled('basic_mobile_admin') && !timesheetOnly, testId: 'manage-btn' },
            { key: 'dockets', icon: FileText, label: 'Dockets', onClick: () => navigate('/mobile/manage/dockets'), show: can('dockets.read') && isFlagEnabled('basic_mobile_admin') && !timesheetOnly, testId: 'dockets-btn' },
            { key: 'chat', icon: MessageSquare, label: 'Chat', onClick: () => navigate('/mobile/chat'), show: hasFeature('messaging') && !timesheetOnly, badge: chatUnreadCount, testId: 'chat-btn' },
            { key: 'notifications', icon: Bell, label: t('mobile.menu.notifications'), onClick: () => navigate('/mobile/notifications'), show: !timesheetOnly, badge: unreadCount, testId: 'notifications-btn' },
            { key: 'compliance', icon: Shield, label: 'Compliance', onClick: () => navigate('/mobile/compliance'), show: (isWorker() || can('compliance.read')) && !timesheetOnly, locked: !hasFeature('compliance'), testId: 'compliance-btn' },
            { key: 'equipment', icon: Truck, label: 'Assets', onClick: () => navigate('/mobile/equipment'), show: (isWorker() || can('equipment.read')) && !timesheetOnly, testId: 'equipment-btn' },
            { key: 'my-dockets', icon: FileText, label: 'My Dockets', onClick: () => navigate('/mobile/my-dockets'), show: isWorker() && !timesheetOnly, testId: 'my-dockets-btn' },
            { key: 'documents', icon: FolderOpen, label: 'Documents', onClick: () => navigate('/mobile/documents'), show: (isWorker() || can('documents.read')) && !timesheetOnly, testId: 'documents-btn' },
            { key: 'quotes', icon: FileCheck, label: 'Quotes', onClick: () => navigate('/mobile/quotes'), show: (hasMobileQuotesAccess() || can('quotes.read')) && !timesheetOnly, locked: !hasMobileQuotesAccess() && !can('quotes.read'), lockedTitle: 'Quoting access not enabled for your account', testId: 'quotes-btn' },
            { key: 'inspections', icon: ClipboardCheck, label: 'Inspections', onClick: () => navigate('/mobile/inspections'), show: !!operator?.is_yard_dispatcher && !timesheetOnly, testId: 'inspections-btn' },
            { key: 'help', icon: HelpCircle, label: 'Help', onClick: () => navigate('/mobile/help'), show: !timesheetOnly, testId: 'help-btn' },
          ].filter(a => a.show && !a.locked);

          if (actions.length === 0) return null;

          return (
            <div className="grid grid-cols-4 gap-3" data-testid="mobile-quick-actions">
              {actions.map((a) => {
                const Icon = a.icon;
                return (
                  <Button
                    key={a.key}
                    variant="outline"
                    className="h-auto py-4 flex flex-col items-center gap-2 bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)] hover:bg-[#ffd108] hover:text-[#1a1a1a] hover:border-[#ffd108] relative"
                    onClick={a.onClick}
                    data-testid={a.testId}
                  >
                    <Icon className="h-6 w-6" />
                    <span className="text-xs">{a.label}</span>
                    {a.badge > 0 && (
                      <span className="absolute top-2 right-3 min-w-[18px] h-[18px] bg-[#ffd108] text-[#1a1a1a] text-xs font-bold rounded-full flex items-center justify-center px-1">
                        {a.badge > 99 ? '99+' : a.badge}
                      </span>
                    )}
                  </Button>
                );
              })}
            </div>
          );
        })()}

        {/* Upcoming Jobs — only for workers with full licence */}
        {isWorker() && !timesheetOnly && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-[var(--m-text)] font-semibold flex items-center gap-2">
              <Calendar className="h-4 w-4 text-[#ffd108]" />
              Upcoming · 7 days
            </h2>
            <Badge variant="secondary" className="bg-[#ffd108] text-[#1a1a1a]">
              {todayJobs.length}
            </Badge>
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
            </div>
          ) : todayJobs.length === 0 ? (
            <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
              <CardContent className="py-8 text-center">
                <Briefcase className="h-12 w-12 mx-auto text-gray-600 mb-3" />
                <p className="text-[var(--m-text-secondary)]">You're clear for the next 7 days. 🎉</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {(() => {
                // Group by viewing_date, preserve server-side ordering.
                const now = new Date();
                const userToday = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
                const tomorrow = new Date(now);
                tomorrow.setDate(tomorrow.getDate() + 1);
                const userTomorrow = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')}`;

                const groups = {};
                for (const job of todayJobs) {
                  const d = job.viewing_date || userToday;
                  (groups[d] ||= []).push(job);
                }

                const formatHeader = (d) => {
                  if (d === userToday) return 'Today';
                  if (d === userTomorrow) return 'Tomorrow';
                  return formatWeekdayShort(d, tenantSettings?.date_format);
                };

                return Object.keys(groups).sort().map((d) => {
                  const isToday = d === userToday;
                  const dayJobs = groups[d];
                  return (
                    <div key={d} data-testid={`upcoming-day-${d}`}>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className={`text-sm font-medium ${isToday ? 'text-[#ffd108]' : 'text-[var(--m-text-secondary)]'}`}>
                          {formatHeader(d)}
                        </h3>
                        <span className="text-xs text-[var(--m-text-muted)]">
                          {dayJobs.length} {dayJobs.length === 1 ? 'job' : 'jobs'}
                        </span>
                      </div>
                      <div className="space-y-2">
                        {dayJobs.map((job) => (
                          <Card
                            key={`${job.id}-${job.viewing_date}`}
                            className={`bg-[var(--m-bg-card)] border-[var(--m-border)] cursor-pointer hover:border-[#ffd108] transition-colors ${isToday ? '' : 'opacity-90'}`}
                            onClick={() => navigate(`/mobile/jobs/${job.id}?date=${job.viewing_date || userToday}`)}
                            data-testid={`upcoming-job-${job.id}-${job.viewing_date}`}
                          >
                            <CardContent className={isToday ? 'p-4' : 'p-3'}>
                              <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <span className="text-[#ffd108] font-bold text-sm">
                                      {formatTime(job.start_time)}{job.end_time ? ` - ${formatTime(job.end_time)}` : ''}
                                    </span>
                                    {job.status === 'sent' && (
                                      <Badge variant="secondary" className="bg-amber-900 text-amber-300 text-xs">
                                        Awaiting ack
                                      </Badge>
                                    )}
                                    {job.has_docket && (
                                      <Badge variant="secondary" className="bg-green-900 text-green-300 text-xs">
                                        Docketed
                                      </Badge>
                                    )}
                                  </div>
                                  <h3 className={`text-[var(--m-text)] font-medium truncate ${isToday ? '' : 'text-sm'}`}>{job.customer_name}</h3>
                                  <p className="text-[var(--m-text-secondary)] text-xs flex items-center gap-1 truncate">
                                    <MapPin className="h-3 w-3 flex-shrink-0" />
                                    <span className="truncate">{job.site_name}</span>
                                  </p>
                                  {isToday && (
                                    <p className="text-[var(--m-text-muted)] text-xs flex items-center gap-1 mt-1" data-testid="job-card-equipment">
                                      <Truck className="h-3 w-3" />
                                      {(() => {
                                        const type = job.equipment_type || job.equipment_name;
                                        const assetId = job.equipment_asset_id;
                                        if (type && assetId) return `${type} · ${assetId}`;
                                        return type || assetId || 'No asset';
                                      })()}
                                    </p>
                                  )}
                                </div>
                                <ChevronRight className="h-5 w-5 text-[var(--m-text-muted)] flex-shrink-0" />
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </div>
        )}

        {/* View All Jobs Button */}
        {isWorker() && !timesheetOnly && (
        <Button 
          variant="outline" 
          className="w-full bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)] hover:bg-[#ffd108] hover:text-[#1a1a1a] hover:border-[#ffd108]"
          onClick={() => navigate('/mobile/jobs')}
        >
          View All Jobs
          <ChevronRight className="h-4 w-4 ml-2" />
        </Button>
        )}
      </div>
      
      {/* PWA Add to Home Screen Prompt */}
      <AddToHomeScreen />
    </MobileLayout>
  );
};

export default MobileDashboard;
